import React, { useState } from "react";
import {
    Box,
    Chip,
    Button,
    Autocomplete,
    TextField,
    Stack,
    Typography,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { CategoryWithCount } from "../../../services/toolsService";

export interface CategoryTerm {
    categoryId: string;
    /** Operator linking this term to the PREVIOUS one. Ignored (treated as the start of a new group) for the first term. */
    operator: "AND" | "OR" | null;
}

interface CategoryChainBuilderProps {
    categories: CategoryWithCount[];
    chain: CategoryTerm[];
    onChange: (chain: CategoryTerm[]) => void;
}

/**
 * Groups a chain into OR-of-AND-groups of category IDs for the API:
 * consecutive AND terms join the current group, OR starts a new one —
 * standard AND-binds-tighter-than-OR precedence (same as SQL
 * `WHERE (a AND b) OR c`). A chain of only ANDs collapses to one group;
 * a chain of only ORs collapses to one group per category.
 */
export function categoryChainToGroups(chain: CategoryTerm[]): string[][] {
    const groups: string[][] = [];
    for (const term of chain) {
        if (term.operator === "AND" && groups.length > 0) {
            groups[groups.length - 1].push(term.categoryId);
        } else {
            groups.push([term.categoryId]);
        }
    }
    return groups;
}

/**
 * Sequential category filter builder: pick a category, then AND/OR, then
 * the next category, and so on — instead of a single global match mode,
 * this lets each pair of categories in the chain have its own relation
 * (e.g. "Kitten AND Can Food" OR "Senior AND Can Food").
 */
export const CategoryChainBuilder: React.FC<CategoryChainBuilderProps> = ({
    categories,
    chain,
    onChange,
}) => {
    // null = not currently picking a category; "AND"/"OR" = the operator
    // that will link the next picked category to the chain.
    const [pendingOperator, setPendingOperator] = useState<
        "AND" | "OR" | null
    >(null);

    const chosenIds = new Set(chain.map((t) => t.categoryId));
    const availableCategories = categories.filter(
        (c) => !chosenIds.has(c.category_id),
    );
    const isPicking = chain.length === 0 || pendingOperator !== null;

    const handlePick = (category: CategoryWithCount | null) => {
        if (!category) return;
        onChange([
            ...chain,
            {
                categoryId: category.category_id,
                operator: chain.length === 0 ? null : pendingOperator,
            },
        ]);
        setPendingOperator(null);
    };

    const handleRemove = (index: number) => {
        onChange(chain.filter((_, i) => i !== index));
        setPendingOperator(null);
    };

    const handleToggleOperator = (index: number) => {
        onChange(
            chain.map((term, i) =>
                i === index
                    ? { ...term, operator: term.operator === "AND" ? "OR" : "AND" }
                    : term,
            ),
        );
    };

    return (
        <Box>
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.75 }}
            >
                Categories
            </Typography>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                useFlexGap
                sx={{ flexWrap: "wrap" }}
            >
                {chain.map((term, i) => {
                    const category = categories.find(
                        (c) => c.category_id === term.categoryId,
                    );
                    return (
                        <Stack
                            key={term.categoryId}
                            direction="row"
                            spacing={1}
                            alignItems="center"
                        >
                            {i > 0 && (
                                <Chip
                                    label={term.operator ?? "AND"}
                                    size="small"
                                    onClick={() => handleToggleOperator(i)}
                                    color={
                                        term.operator === "OR"
                                            ? "secondary"
                                            : "primary"
                                    }
                                    variant="outlined"
                                    sx={{ fontWeight: 700, cursor: "pointer" }}
                                    title="Click to switch AND/OR"
                                />
                            )}
                            <Chip
                                label={
                                    category
                                        ? `${category.name} (${category.product_count})`
                                        : term.categoryId
                                }
                                size="small"
                                onDelete={() => handleRemove(i)}
                            />
                        </Stack>
                    );
                })}

                {isPicking ? (
                    <Autocomplete
                        size="small"
                        options={availableCategories}
                        getOptionLabel={(c) => `${c.name} (${c.product_count})`}
                        onChange={(_, value) => handlePick(value)}
                        sx={{ minWidth: 240 }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={
                                    chain.length === 0
                                        ? "Select a category..."
                                        : `Select category to ${pendingOperator}...`
                                }
                                autoFocus={chain.length > 0}
                            />
                        )}
                    />
                ) : (
                    <Stack direction="row" spacing={1}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={() => setPendingOperator("AND")}
                            disabled={availableCategories.length === 0}
                        >
                            AND
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<Add />}
                            onClick={() => setPendingOperator("OR")}
                            disabled={availableCategories.length === 0}
                        >
                            OR
                        </Button>
                    </Stack>
                )}
            </Stack>

            {chain.length === 0 && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.75, display: "block" }}
                >
                    Pick a category, then use AND/OR to combine more
                </Typography>
            )}
            {chain.length > 1 && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.75, display: "block" }}
                >
                    Click an AND/OR chip to switch it
                </Typography>
            )}
        </Box>
    );
};
